import styled, { css } from "styled-components"
import colors from "@constants/colors"
import { timings, cubicBezier } from "@constants/animation"
import { subtext } from "@constants/typography"
import { IconWrapper } from "@components/icons/utils"

export const baseButtonStyles = css`
  padding: 0;
  outline: none;
  border: none;
  background: none;

  &:focus,
  &:active {
    outline: none;
  }
`

export const openProps = css`
  transform: translateX(0);
  transition: transform ${timings.medium} ${cubicBezier};
`

export const Container = styled.div<{ isOpen?: boolean }>`
  ${({ isOpen }) => css`
    z-index: 3;
    position: absolute;
    top: 0;
    flex-direction: column;
    display: flex;
    opacity: 1;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: translateX(100%);
    transition: transform ${timings.medium} ${cubicBezier};
    will-change: transform;
    width: 100%;
    height: 100%;
    background-color: ${colors.snow};
    cursor: default;

    ${isOpen ? openProps : ""}
  `}
`

export const overlayActiveProps = css`
  opacity: 0.1;
  transition: opacity ${timings.medium} ${cubicBezier};
`

export const Overlay = styled.div<{ isVisible: boolean }>`
  ${({ isVisible }) => css`
    z-index: 2;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity ${timings.long} ${cubicBezier};
    will-change: opacity;
    height: 100%;
    width: 100%;
    background-color: ${colors.black};
    pointer-events: none;

    ${isVisible ? overlayActiveProps : ""}
  `}
`

export const CloseButton = styled.button`
  ${baseButtonStyles}
  align-items: center;
  justify-content: center;
  display: flex;
  width: 32px;
  height: 32px;
`

export const Header = styled.div`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  display: flex;
`

export const Body = styled.div`
  display: block;
  font-family: "Jost", sans-serif;
  padding: 30px 0;

  button {
    background: #ff1c24;
    color: #fff;
    min-width: 150px;
    text-transform: uppercase;
    border: 0;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: 0.6s ease-out;
    width: 100%;
    padding: 15px;

    &:hover {
      color: #ff1c24;
      background: #fff;
    }

    &:disabled {
      background: ${colors.mediumGray};
      cursor: not-allowed;
    }
  }
`

export const Content = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 10px 40px;
`

export const Filters = styled.div`
  display: block;
  width: 100%;
`

export const Filter = styled.div<{ disabled?: boolean }>`
  min-width: 150px;
  font-family: "Jost", sans-serif;
  flex-grow: 0;
  margin-bottom: 10px;

  ${({ disabled }) => css`
    pointer-events: ${disabled ? "none" : "all"};

    [class*="SelectInput__Container"] {
      background-color: ${disabled ? colors.mediumGray : colors.lightestGray};
    }
  `}

  [class*="SelectInput__Container"] {
    padding: 0 8px;
    height: 50px;
  }

  [class*="MuiInput-formControl"] {
    margin-top: 22px;
    height: 16px;
    font-size: 14px;
  }

  [class*="MuiInputLabel-root"] {
    font-family: "Jost", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    line-height: 16px;
    transform: translate(0, 18px) scale(1);
  }

  .MuiInputLabel-shrink {
    transform: translate(0, 4px) scale(0.6) !important;
  }

  .MuiNativeSelect-icon {
    top: 0px;
  }
`

export const Label = styled.div`
  color: ${colors.obsidian};
  margin-bottom: 0;
  text-transform: uppercase;
  font-family: "Jost", sans-serif;
  text-align: center;

  > div {
    font-size: 20px;
    font-weight: 600;
  }

  > h1 {
    font-size: 25px;
    line-height: 25px;
    font-weight: 400;
    margin: 8px 0 16px 0;
  }
`

export const Footer = styled.div`
  ${subtext}
  align-items: center;
  justify-content: center;
  display: flex;
  color: ${colors.slateDark};
  padding: 10px 40px;

  a {
    text-decoration: underline;
    color: ${colors.red};
    font-weight: 600;
  }

  ${IconWrapper} {
    opacity: 0.4;
    width: 20px;
    height: 20px;
    margin-right: 20px;
    border: 1.5px solid ${colors.slateLight};
    border-radius: 50%;
  }
`
